/**
 * Card Component
 * Container component for grouping related content
 */

import { HTMLAttributes, forwardRef } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card variant */
  variant?: 'default' | 'bordered' | 'elevated';

  /** Card padding */
  padding?: 'none' | 'sm' | 'md' | 'lg';

  /** Clickable card */
  clickable?: boolean;

  /** Selected state */
  selected?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      clickable = false,
      selected = false,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    // Base styles
    const baseStyles = 'ww-rounded-lg ww-bg-white';

    // Variant styles
    const variantStyles = {
      default: 'ww-border ww-border-gray-200',
      bordered: 'ww-border-2 ww-border-gray-300',
      elevated: 'ww-shadow-md',
    };

    // Padding styles
    const paddingStyles = {
      none: '',
      sm: 'ww-p-3',
      md: 'ww-p-4',
      lg: 'ww-p-6',
    };

    // Interactive styles
    const interactiveStyles = clickable
      ? 'ww-cursor-pointer ww-transition-all hover:ww-shadow-lg focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary focus:ww-ring-offset-2'
      : '';

    // Selected styles
    const selectedStyles = selected
      ? 'ww-border-primary ww-ring-2 ww-ring-primary'
      : '';

    const classes = `${baseStyles} ${variantStyles[variant]} ${paddingStyles[padding]} ${interactiveStyles} ${selectedStyles} ${className}`;

    return (
      <div
        ref={ref}
        className={classes}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
